import { UrlHelpers } from "@/shared/services/UrlHelpers";
import { DraftSyncVersion } from "./DraftSyncVersion";

export class VersionScreenshotInfo {
    projectId: string;
    deviceId: string;
    timestamp: number;
    
    private _url: string;
    private _localUrl: string;
    
    private base64: string | null;

    async getUrl(): Promise<string> {
        if (this._localUrl) {
            return Promise.resolve(this._localUrl);
        }
        if (this.base64) {
            this._localUrl = await UrlHelpers.base64ToBlobUrl(this.base64);
            return this._localUrl
        }
        
        return this._url;
    }

    constructor(projectId: string, deviceId: string, timestamp: number, url: string) {
        this.projectId = projectId;
        this.deviceId = deviceId;
        this.timestamp = timestamp;
        this._url = url;
        this.base64 = null;
    }

    static fromVersion(version: DraftSyncVersion, url: string): VersionScreenshotInfo {
        return new VersionScreenshotInfo(version.draftId, version.updateDeviceId, version.roundedTimestamp, url);
    }

    isMatching(version: DraftSyncVersion): boolean {
        return this.projectId === version.draftId && this.deviceId === version.updateDeviceId && this.timestamp === version.roundedTimestamp;
    }

    static versionId(version: DraftSyncVersion, isFullRes: boolean = true): string {
        let urlKey = `project: ${version.draftId}, device: ${version.updateDeviceId}`;
        if (isFullRes) {
            urlKey = `${urlKey}, fullRes: true`;
        }
        return urlKey;
    }

    static fromJSONDictionary(data: any): {[key: string]: VersionScreenshotInfo} {
        let parsed = JSON.parse(data)
        let result = {};
        for (let key in parsed) {
            result[key] = this.fromJSON(parsed[key])
        }
        return result
    }

    static fromJSON(data: any): VersionScreenshotInfo {
        const json = (typeof data === 'string') ? JSON.parse(data) : data;
        let versionScreenshotInfo = new VersionScreenshotInfo(
            json.projectId,
            json.deviceId,
            json.timestamp,
            json.url
        );
        versionScreenshotInfo.base64 = json.base64
        return versionScreenshotInfo
    }

    async persistLocalBlobs(abortSignal?: AbortSignal){
        // if there's no underlying base64 and the url if of blob type
        if (this.base64) {
            return;
        }
        let base64Image = await this.urlToBase64(this._url, abortSignal)
        this.base64 = base64Image
    }
    
    async urlToBase64(url: string, abortSignal?: AbortSignal): Promise<string | null> {
        try {
            // Check if already aborted
            if (abortSignal?.aborted) {
                throw new DOMException('Operation aborted', 'AbortError');
            }

            const response = await fetch(url, { signal: abortSignal });
            
            // Check if aborted after fetch
            if (abortSignal?.aborted) {
                throw new DOMException('Operation aborted', 'AbortError');
            }
            
            const blob = await response.blob();

            // Check if aborted after blob conversion
            if (abortSignal?.aborted) {
                throw new DOMException('Operation aborted', 'AbortError');
            }

            return await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();

                reader.onloadend = () => {
                    if (typeof reader.result === "string") {
                        resolve(reader.result);
                    } else {
                        reject("Failed to convert to Base64");
                    }
                };

                reader.onerror = () => reject("FileReader error");
                reader.readAsDataURL(blob);
            });
        } catch (err) {
            if (err.name === 'AbortError') {
                throw err; // Re-throw abort errors
            }
            console.error("Error converting URL to Base64:", err);
            return Promise.resolve(null);
        }
    }

    toJSON(): any {
        return {
            projectId: this.projectId,
            deviceId: this.deviceId,
            timestamp: this.timestamp,
            url: this._url,
            base64: this.base64
        };
    }

    toString(): string {
        return `project: ${this.projectId}, device: ${this.deviceId}, timestamp: ${this.timestamp}, url: ${this._url}`;
    }
}